// highlights-slice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    data: {},
    loading: false,
    error: null
};
// Helper function to create a unique key for document_id and document_type
const getDocumentKey = (document_id, document_type) => `${document_id}-${document_type}`;
export const fetch_highlights = createAsyncThunk('highlights/fetch_highlights', async (params, { rejectWithValue }) => {
    if (!params.document_id?.trim()) {
        return { document_id: params.document_id, document_type: params.document_type, highlights: [] };
    }
    try {
        const response = await axiosInstance.get(`/highlights/${params.document_id}`, {
            params: { document_type: params.document_type }
        });
        const envelope = response.data;
        const payload = envelope.data ?? { document_id: params.document_id, document_type: params.document_type, highlights: [] };
        return { document_id: payload.document_id, document_type: payload.document_type, highlights: payload.highlights };
    }
    catch (error) {
        return rejectWithValue(error.message);
    }
});
export const add_highlight = createAsyncThunk('highlights/add_highlight', async (highlight, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/highlights', highlight);
        const envelope = response.data;
        return envelope.data ?? response.data;
    }
    catch (error) {
        return rejectWithValue(error.message);
    }
});
export const update_highlight = createAsyncThunk('highlights/update_highlight', async (highlight, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/highlights/${highlight.id}`, highlight);
        const envelope = response.data;
        return envelope.data ?? response.data;
    }
    catch (error) {
        return rejectWithValue(error.message);
    }
});
export const delete_highlight = createAsyncThunk('highlights/delete_highlight', async (highlight_id, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/highlights/${highlight_id}`);
        return highlight_id;
    }
    catch (error) {
        return rejectWithValue(error.message);
    }
});
const highlights_slice = createSlice({
    name: 'highlights',
    initialState,
    reducers: {
        add_highlight: (state, action) => {
            const { document_id, document_type } = action.payload;
            const documentKey = getDocumentKey(document_id, document_type);
            if (!state.data[documentKey])
                state.data[documentKey] = [];
            state.data[documentKey].push(action.payload);
        },
        remove_highlight: (state, action) => {
            const { document_id, document_type, highlight_id } = action.payload;
            const documentKey = getDocumentKey(document_id, document_type);
            if (state.data[documentKey]) {
                state.data[documentKey] = state.data[documentKey].filter(h => h.id !== highlight_id);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch highlights
            .addCase(fetch_highlights.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetch_highlights.fulfilled, (state, action) => {
            state.loading = false;
            const { document_id, document_type, highlights } = action.payload;
            const documentKey = getDocumentKey(document_id, document_type);
            state.data[documentKey] = highlights;
        })
            .addCase(fetch_highlights.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
            // Add highlight
            .addCase(add_highlight.fulfilled, (state, action) => {
            const highlight = action.payload;
            const documentKey = getDocumentKey(highlight.document_id, highlight.document_type);
            if (!state.data[documentKey])
                state.data[documentKey] = [];
            state.data[documentKey].push(highlight);
        })
            // Update highlight
            .addCase(update_highlight.fulfilled, (state, action) => {
            const updatedHighlight = action.payload;
            const documentKey = getDocumentKey(updatedHighlight.document_id, updatedHighlight.document_type);
            if (state.data[documentKey]) {
                state.data[documentKey] = state.data[documentKey].map(h => h.id === updatedHighlight.id ? { ...h, ...updatedHighlight } : h);
            }
        })
            // Delete highlight
            .addCase(delete_highlight.fulfilled, (state, action) => {
            const highlight_id = action.payload;
            // Remove from all document keys
            Object.keys(state.data).forEach(key => {
                state.data[key] = state.data[key].filter(h => h.id !== highlight_id);
            });
        });
    }
});
export const { add_highlight: addHighlightAction, remove_highlight } = highlights_slice.actions;
export default highlights_slice.reducer;
